import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import Reveal, { Overline } from "./Reveal";

const REGISTRATIONS = [
  "Royal Malaysian Customs Department",
  "Malaysian Immigration Department",
  "Malaysian Marine Department",
  "Johor Port Berhad — Agency, Bunker, Support & Tug Charter",
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" data-testid="about" className="relative py-24 lg:py-32">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:gap-10 lg:px-12">
        <div className="lg:col-span-6">
          <Reveal>
            <Overline>Who We Are</Overline>
            <h2 className="mt-6 font-display text-4xl font-medium uppercase leading-[0.95] tracking-tighter sm:text-5xl">
              Two decades
              <br />
              <span className="font-serif italic font-medium tracking-normal">
                on the water
              </span>
              <span className="text-brand">.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-steel sm:text-lg">
              UDAT MARINE (JM 0340759-U) was established in mid-2001 in Johor Bahru.
              We are a 100% Bumiputera shipping and forwarding agency — and a marine
              salvage contractor that undertakes salvage and recovery jobs with
              partners across the Asian region.
            </p>
            <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-steel sm:text-lg">
              From our base at Pekan Pengerang we deliver efficient, locally-savvy
              solutions for every vessel and crew need — from rapid shipping
              clearance to expert handling of detained cases.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-steel">
              Registered with
            </p>
            <ul className="mt-4 divide-y divide-line border-y border-line">
              {REGISTRATIONS.map((r) => (
                <li
                  key={r}
                  className="flex items-center gap-3 py-3.5 text-sm text-slate-200"
                >
                  <BadgeCheck size={15} className="shrink-0 text-brand" />
                  {r}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div ref={ref} className="lg:col-span-6">
          <Reveal delay={0.2} className="relative h-full">
            <div className="relative h-[420px] overflow-hidden border border-line lg:h-full lg:min-h-[520px]">
              <motion.img
                src="/images/unsplash-cargo.jpg"
                alt="Cargo vessel at sea"
                style={{ y }}
                className="h-full w-full scale-110 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 bg-brand px-6 py-5">
              <div className="font-display text-3xl font-semibold leading-none tracking-tight text-white">
                EST. 2001
              </div>
              <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.24em] text-white/80">
                Johor Bahru → Pengerang
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
